// src/lib/facture-envoi-ecran.js — le geste « Envoyer au propriétaire » de
// l'onglet Facturation, côté ÉCRAN, autour du flux emettreEtEnvoyerFacture
// (src/lib/facture-send-flow.js). P-2.46 bis, relecture équipe rouge #1732.
//
// Pourquoi un module : depuis que le numéro est tiré par le serveur, un refus
// ou une réponse perdue laisse une pièce PRÉPARÉE en base (brouillon, ou
// peut-être déjà numérotée). Si la liste des factures ne se relit pas (réseau),
// la garde anti-doublon de l'écran (findBlockingCommission sur la liste en
// mémoire) ne la voit pas : un second clic créerait un second brouillon, voire
// tirerait un second numéro. D'où un blocage (logement, mois) POUR LA SESSION,
// posé quoi que dise la relecture de la liste — même esprit que
// bloquerEnvoiSession (App.jsx) pour un courriel incertain. Recharger la page
// le lève : la liste relue en base fait alors foi.
//
// Fonctions pures ou à dépendances injectées : testables sans React.
// Aucun fichier partagé n'est modifié (invoice-guard.js reste tel quel).

import { findBlockingCommission } from "./invoice-guard";
import { isDraftInvoice } from "./invoices";

// Même texte que la fenêtre d'envoi (App.jsx, ouvrirEnvoiFacture) : une seule source.
export const MSG_ENVOI_BROUILLON =
  "Brouillon en cours pour ce mois : émets-le (bouton « Émettre ») ou supprime-le avant d'envoyer.";

export const MSG_MOIS_A_VERIFIER =
  "Une facture a déjà été préparée pour ce logement et ce mois pendant cette session. Recharge la page et regarde l'Historique avant de recommencer.";

// Lot A3 : la BASE refuse une seconde facture de conciergerie pour le même
// logement et le même mois (index uq_facture_proprio_par_mois, migration
// 20261030130000). Ce refus n'est PAS une panne : la pièce existe déjà
// (autre onglet, vieil onglet, double clic passé entre les gardes).
export const MSG_FACTURE_DEJA_EXISTANTE =
  "Une facture existe déjà pour ce logement et ce mois (préparée ailleurs, par exemple dans un autre onglet). Rien n'a été émis ni envoyé. La liste des factures est relue : regarde l'Historique.";

// 23505 = unique_violation. Sur l'INSERT d'un brouillon (numéro NULL), seul
// l'index logement + mois peut le lever : rien n'a été inséré.
export function estFactureDejaExistante(e) {
  return String((e && e.code) || "") === "23505";
}

export function cleMoisFacture(logId, monthLabel) {
  return `${logId}|${monthLabel}`;
}

/**
 * Refus avant tout geste qui créerait une pièce de commission (ouvrir la
 * fenêtre d'envoi, envoyer, générer un brouillon). Rend null ou le message.
 *   1. la liste montre une pièce : brouillon sans numéro ⇒
 *      MSG_ENVOI_BROUILLON (l'émettre ou le
 *      supprimer, jamais « supprimez la facture ») ;
 *      facture numérotée ⇒ le message historique, inchangé ;
 *   2. la liste ne montre rien mais (logement, mois) est bloqué pour la
 *      session (liste peut-être pas relue) ⇒ « recharge la page ».
 */
export function refusFactureMensuelle({
  invoicesRaw,
  logId,
  logNom,
  monthLabel,
  moisBloques,
}) {
  const bloquante = findBlockingCommission(invoicesRaw, { logId, monthLabel });
  if (!bloquante) {
    if (moisBloques && moisBloques.has(cleMoisFacture(logId, monthLabel)))
      return MSG_MOIS_A_VERIFIER;
    return null;
  }
  if (isDraftInvoice(bloquante)) return MSG_ENVOI_BROUILLON;
  return `⛔ Une facture commission existe déjà pour ${logNom} — ${monthLabel}. Supprimez-la d'abord pour ré-émettre.`;
}

/**
 * Badge « factures en attente » : un logement reste EN ATTENTE tant que sa
 * pièce du mois n'a pas de numéro (brouillon préparé, pas encore émis).
 * Calculé ici plutôt qu'en changeant hasCommissionInvoice (invoice-guard.js,
 * miroir serveur partagé) : la garde anti-doublon, elle, continue de bloquer
 * sur un brouillon.
 */
export function factureMensuelleEnAttente(rows, { logId, monthLabel }) {
  const bloquante = findBlockingCommission(rows, { logId, monthLabel });
  return !bloquante || isDraftInvoice(bloquante);
}

async function relireSansEchec(relireListe) {
  try {
    await relireListe();
  } catch (e) {
    console.error(
      "[facture-envoi-ecran] liste des factures non relue :",
      e?.message || e,
    );
  }
}

/**
 * Lot A3 : la base a refusé le brouillon (23505, une facture existe déjà pour
 * ce logement et ce mois). Rend false si `e` est une autre erreur (l'appelant
 * garde son traitement). Sinon : mois bloqué pour la session, message clair
 * (jamais « Erreur »), fenêtre fermée, liste relue ; rien d'émis, aucun courriel
 * (le refus arrive à l'INSERT du brouillon, avant toute émission).
 */
export async function signalerFactureDejaExistante({
  e,
  moisBloques,
  logId,
  monthLabel,
  relireListe,
  alerter,
  fermer,
}) {
  if (!estFactureDejaExistante(e)) return false;
  moisBloques.add(cleMoisFacture(logId, monthLabel));
  alerter(MSG_FACTURE_DEJA_EXISTANTE);
  fermer();
  await relireSansEchec(relireListe);
  return true;
}

/**
 * Le geste, autour du flux. Dépendances :
 *   lancer()      → Promise<résultat d'emettreEtEnvoyerFacture> (peut lever) ;
 *   relireListe() → Promise (refreshInvoices) — son échec ne lève jamais le blocage ;
 *   fermer()      → setConfirm(null) ;
 *   alerter(msg)  → alert.
 * `moisBloques` : un Set (useRef) qui vit le temps de la session.
 *
 * Le mois est bloqué dès que le flux a pu créer une pièce :
 *   · refus ou réponse perdue APRÈS le brouillon (`brouillonCree`) ;
 *   · exception (réseau, serveur) : on ne sait pas ce qui existe en base ;
 *   · succès : la pièce existe, même si la liste ne se relit pas.
 * Un refus d'AVANT le brouillon (TVA à l'écran, courriel, montant) ne bloque
 * rien et laisse la fenêtre ouverte, comme avant.
 */
export async function envoyerFactureMensuelleGardee({
  invoicesRaw,
  logId,
  logNom,
  monthLabel,
  moisBloques,
  lancer,
  relireListe,
  fermer,
  alerter,
}) {
  for (const [nom, fn] of Object.entries({
    lancer,
    relireListe,
    fermer,
    alerter,
  })) {
    if (typeof fn !== "function")
      throw new Error(`envoyerFactureMensuelleGardee : ${nom} requis`);
  }
  if (!moisBloques || typeof moisBloques.add !== "function")
    throw new Error("envoyerFactureMensuelleGardee : moisBloques requis");

  const refus = refusFactureMensuelle({
    invoicesRaw,
    logId,
    logNom,
    monthLabel,
    moisBloques,
  });
  if (refus) {
    alerter(refus);
    fermer();
    return { ok: false, raison: refus, refuseAvant: true };
  }
  const cle = cleMoisFacture(logId, monthLabel);
  let r;
  try {
    r = await lancer();
  } catch (e) {
    // Lot A3 : la base a refusé le brouillon (facture déjà là pour ce mois) :
    // un refus, pas une panne. Rien d'émis, aucun courriel.
    if (
      await signalerFactureDejaExistante({
        e,
        moisBloques,
        logId,
        monthLabel,
        relireListe,
        alerter,
        fermer,
      })
    )
      return {
        ok: false,
        raison: MSG_FACTURE_DEJA_EXISTANTE,
        dejaExistante: true,
      };
    // L'appelant dit « relis l'Historique » (son catch) ; le mois reste bloqué.
    moisBloques.add(cle);
    throw e;
  }
  if (r && r.ok === true) {
    moisBloques.add(cle);
    return r;
  }
  alerter("⛔ " + ((r && r.raison) || "Envoi refusé."));
  if (r && r.brouillonCree) {
    moisBloques.add(cle);
    fermer();
    await relireSansEchec(relireListe);
  }
  return r;
}
